import { useEffect, useState } from "react";
import api from "../../lib/api";
import { errorMessage } from "../../lib/errorMessage";
import { INTERACTION_TYPES, STAGE_LABEL } from "../../lib/pipeline";
import { REOPEN_STAGES } from "../../lib/pipelineCard";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";

// Two short in-context actions of a Pipeline card. Each calls the SAME route as the prospect page (same rights, same checks).
export function QuickNoteDialog({ prospect, onClose, onDone }) {
  const [kind, setKind] = useState("note");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (prospect) { setKind("note"); setContent(""); } }, [prospect]);

  const save = async () => {
    if (saving || !content.trim()) return;
    setSaving(true);
    try {
      await api.post(`/prospects/${prospect.id}/interactions`, { kind, content: content.trim() });
      toast.success("Note enregistrée");
      onDone(prospect);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!prospect} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ajouter une note</DialogTitle>
          <p className="text-sm text-slate-600 -mt-1">{prospect?.pharmacy_name}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="quick-note-kind">Type d'échange</Label>
            <Select value={kind} onValueChange={setKind}>
              <SelectTrigger id="quick-note-kind" data-testid="quick-note-kind"><SelectValue /></SelectTrigger>
              <SelectContent>{INTERACTION_TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quick-note-content">Contenu</Label>
            <Textarea id="quick-note-content" data-testid="quick-note-content" rows={4} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Ce qui s'est dit, ce qui est convenu…" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving || !content.trim()} data-testid="quick-note-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function QuickAssignDialog({ prospect, directory, onClose, onDone }) {
  const [userId, setUserId] = useState("__none__");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (prospect) setUserId(prospect.assigned_to || "__none__"); }, [prospect]);

  const save = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.post(`/prospects/${prospect.id}/assign`, { user_id: userId === "__none__" ? null : userId });
      toast.success(userId === "__none__" ? "Prospect désassigné" : "Responsable mis à jour");
      onDone(prospect);
      onClose();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!prospect} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Changer de responsable</DialogTitle>
          <p className="text-sm text-slate-600 -mt-1">{prospect?.pharmacy_name}</p>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="quick-assign">Responsable</Label>
          <Select value={userId} onValueChange={setUserId}>
            <SelectTrigger id="quick-assign" data-testid="quick-assign-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Non assigné</SelectItem>
              {directory.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={saving} data-testid="quick-assign-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// « Rouvrir » a lost prospect: the person chooses the commercial stage to resume from (no default imposed); the usual status dialog
// that follows asks for the note explaining why, and the journal keeps « Perdu → étape » in the prospect's history.
export function ReopenDialog({ prospect, onClose, onChoose }) {
  const [stage, setStage] = useState("");
  useEffect(() => { if (prospect) setStage(""); }, [prospect]);
  return (
    <Dialog open={!!prospect} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rouvrir ce prospect</DialogTitle>
          <p className="text-sm text-slate-600 -mt-1">{prospect?.pharmacy_name}</p>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label htmlFor="reopen-stage">Reprendre à l'étape</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger id="reopen-stage" data-testid="reopen-stage"><SelectValue placeholder="Choisir une étape" /></SelectTrigger>
            <SelectContent>{REOPEN_STAGES.map((id) => <SelectItem key={id} value={id}>{STAGE_LABEL[id]}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-xs text-slate-600">Le motif de perte « {prospect?.loss_reason || "non renseigné"} » reste dans l'historique du prospect.</p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button disabled={!stage} onClick={() => onChoose(prospect, stage)} data-testid="reopen-continue">Continuer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
